'use client';
import React, { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { Toaster } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';
import {
  Eye, EyeOff, Check, Building2, MapPin, Shield, Copy, ChevronRight,
  Star, Users, BarChart3, Lock, Mail, User, Zap, UserCheck, Hammer, Handshake,
  CheckCircle2, AlertCircle, Loader2, Phone
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';


type LoginForm = { email: string; password: string; remember: boolean };
type ProfileType = 'end_user' | 'developer_builder' | 'channel_partner';

type SignupForm = {
  fullName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  city: string;
  profileType: ProfileType;
  referralCode: string;
  terms: boolean;
};

const demoCredentials = [
  { role: 'Client', email: 'rajesh.mehta@propdatax.com', password: 'Client@2026' },
  { role: 'Admin', email: 'admin@propdatax.com', password: 'Admin@2026' },
];

const marketStats = [
  { label: 'Projects Tracked', value: '214', sub: 'Across 18 micro-markets' },
  { label: 'Avg. Inventory Sold', value: '67.4%', sub: 'Q1 2026 Pune average' },
  { label: 'Price Growth YoY', value: '+11.2%', sub: 'Residential average 2025' },
  { label: 'Active Subscribers', value: '1,840', sub: 'Consultants & investors' },
];

const microMarkets = [
  'Hinjewadi', 'Wakad', 'Baner', 'Kharadi', 'Koregaon Park',
  'Punawale', 'Undri', 'Wagholi', 'Ravet', 'Kalyani Nagar',
  'Tathawade', 'NIBM Road',
];

const planFeatures = {
  monthly: ['Access to 214 project profiles', 'Inventory absorption data', 'Price trend charts', 'RERA number lookup'],
  yearly: ['Everything in Monthly', 'Priority data refresh', 'Micro-market comparison tool', 'Downloadable PDF reports'],
};

const profileTypes: { type: ProfileType; label: string; icon: React.ElementType; desc: string; color: string }[] = [
  { type: 'end_user', label: 'End User', icon: UserCheck, desc: 'Home buyer or investor', color: 'from-primary/80 to-primary' },
  { type: 'developer_builder', label: 'Developer / Builder', icon: Hammer, desc: 'Real estate developer', color: 'from-violet-600 to-violet-800' },
  { type: 'channel_partner', label: 'Channel Partner', icon: Handshake, desc: 'Broker or consultant', color: 'from-amber-600 to-amber-800' },
];

export default function AuthPanel() {
  const router = useRouter();
  const { signIn, signUp } = useAuth();
  const [tab, setTab] = useState<'login' | 'signup'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [signupLoading, setSignupLoading] = useState(false);
  const [loginCooldown, setLoginCooldown] = useState(0);
  const [selectedProfileType, setSelectedProfileType] = useState<ProfileType>('end_user');
  const [referralValidating, setReferralValidating] = useState(false);
  const [referralStatus, setReferralStatus] = useState<'idle' | 'valid' | 'invalid'>('idle');
  const [referralReferrerName, setReferralReferrerName] = useState('');

  const loginForm = useForm<LoginForm>({ defaultValues: { remember: false } });
  const signupForm = useForm<SignupForm>({ defaultValues: { profileType: 'end_user', referralCode: '' } });

  const showReferralField = selectedProfileType === 'end_user' || selectedProfileType === 'channel_partner';

  const validateReferralCode = useCallback(async (code: string, email: string) => {
    if (!code || code.length < 4) { setReferralStatus('idle'); return; }
    setReferralValidating(true);
    try {
      const res = await fetch('/api/referral/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ referralCode: code, userEmail: email }),
      });
      const data = await res.json();
      if (data.valid) {
        setReferralStatus('valid');
        setReferralReferrerName(data.referrerName || '');
      } else {
        setReferralStatus('invalid');
        setReferralReferrerName('');
      }
    } catch {
      setReferralStatus('invalid');
    } finally {
      setReferralValidating(false);
    }
  }, []);

  const handleLogin = async (data: LoginForm) => {
    if (loginCooldown > 0) return;
    setLoginLoading(true);
    try {
      const result = await signIn(data.email, data.password);
      toast.success('Welcome back!');
      const supabase = createClient();
      const userId = result?.user?.id;
      if (userId) {
        const { data: profileData } = await supabase.from('user_profiles').select('role').eq('id', userId).maybeSingle();
        const isAdmin = profileData?.role === 'admin' || result?.user?.user_metadata?.role === 'admin' || result?.user?.app_metadata?.role === 'admin';
        if (isAdmin) { router.push('/project-listings-screen'); return; }
        const { data: subData } = await supabase.from('subscriptions').select('status, expires_at').eq('user_id', userId).order('created_at', { ascending: false }).limit(1).maybeSingle();
        const isActive = subData?.status === 'active' && (!subData.expires_at || new Date(subData.expires_at) > new Date());
        router.push(isActive ? '/project-listings-screen' : '/subscription');
      } else {
        router.push('/subscription');
      }
    } catch (err: any) {
      const msg: string = err?.message || '';
      const isRateLimit = msg.toLowerCase().includes('rate limit') || msg.toLowerCase().includes('too many');
      if (isRateLimit) {
        toast.error('Too many login attempts. Please wait 60 seconds.');
        let s = 60; setLoginCooldown(s);
        const iv = setInterval(() => { s -= 1; setLoginCooldown(s); if (s <= 0) clearInterval(iv); }, 1000);
      } else {
        toast.error(msg || 'Invalid credentials. Please try again.');
        let s = 5; setLoginCooldown(s);
        const iv = setInterval(() => { s -= 1; setLoginCooldown(s); if (s <= 0) clearInterval(iv); }, 1000);
      }
    } finally {
      setLoginLoading(false);
    }
  };

  const handleSignupSubmit = async (data: SignupForm) => {
    if (data.password !== data.confirmPassword) {
      signupForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    if (showReferralField && data.referralCode && referralStatus === 'invalid') {
      toast.error('Please enter a valid Referral ID or leave it blank');
      return;
    }
    await completeSignup(data);
  };

  const completeSignup = async (data: SignupForm) => {
    setSignupLoading(true);
    try {
      const supabase = createClient();

      // Create account directly via signUp
      await signUp(data.email, data.password, {
        fullName: data.fullName,
        phone: data.phone,
        profileType: data.profileType,
        city: data.city,
        referredBy: showReferralField && referralStatus === 'valid' ? data.referralCode.toUpperCase() : null,
      });

      // Update profile with extra fields
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.from('user_profiles').upsert({
          id: user.id,
          full_name: data.fullName,
          email: data.email,
          phone: data.phone,
          profile_type: data.profileType,
          city: data.city,
          referred_by: showReferralField && referralStatus === 'valid' ? data.referralCode.toUpperCase() : null,
          role: 'client',
        }, { onConflict: 'id' });

        // If referred, create referral record
        if (showReferralField && referralStatus === 'valid' && data.referralCode) {
          const { data: referrer } = await supabase
            .from('user_profiles')
            .select('id')
            .eq('referral_code', data.referralCode.toUpperCase())
            .maybeSingle();
          if (referrer) {
            await supabase.from('referrals').insert({
              referrer_id: referrer.id,
              referred_user_id: user.id,
              referral_code: data.referralCode.toUpperCase(),
              referred_email: data.email,
              status: 'pending',
            });
          }
        }
      }

      toast.success('Account created! Redirecting to plan selection...');
      router.push('/plan-selection');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create account. Please try again.');
    } finally {
      setSignupLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const autofill = (cred: typeof demoCredentials[0]) => {
    loginForm.setValue('email', cred.email);
    loginForm.setValue('password', cred.password);
    toast.info(`${cred.role} credentials filled`);
  };

  return (
    <div className="min-h-screen bg-background flex">
      <Toaster position="bottom-right" richColors />

      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-[52%] xl:w-[55%] flex-col bg-gradient-to-br from-background via-card to-secondary relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-primary/5 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col h-full p-10 xl:p-14">
          <div className="flex items-center gap-3 mb-12">
            <AppLogo size={40} />
            <div>
              <span className="font-bold text-xl text-foreground tracking-tight">PropdataX</span>
              <p className="text-[11px] text-muted-foreground tracking-widest uppercase">Pune Market Intelligence</p>
            </div>
          </div>

          <div className="mb-10">
            <h1 className="text-3xl xl:text-4xl font-bold text-foreground leading-tight mb-3">
              Pune&apos;s Most{' '}
              <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">Data-Dense</span>{' '}
              Real Estate Platform
            </h1>
            <p className="text-muted-foreground text-base leading-relaxed max-w-md">
              Track inventory absorption, price trends, and developer credibility across 214 residential projects in 18 Pune micro-markets.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-8">
            {marketStats.map((stat) => (
              <div key={`stat-${stat.label}`} className="bg-card/60 border border-border rounded-xl p-4">
                <p className="metric-value text-2xl text-foreground">{stat.value}</p>
                <p className="text-xs font-semibold text-foreground mt-0.5">{stat.label}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">{stat.sub}</p>
              </div>
            ))}
          </div>

          <div className="mb-8">
            <p className="text-[11px] text-muted-foreground uppercase tracking-widest font-semibold mb-3 flex items-center gap-2">
              <MapPin size={12} /> Covered Micro-Markets
            </p>
            <div className="flex flex-wrap gap-2">
              {microMarkets.map((m) => (
                <span key={`market-${m}`} className="text-[11px] font-medium px-2.5 py-1 bg-card/60 border border-border rounded-full text-muted-foreground">{m}</span>
              ))}
            </div>
          </div>

          <div className="mt-auto">
            <p className="text-[11px] text-muted-foreground uppercase tracking-widest font-semibold mb-3">Subscription Plans</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-card/60 border border-border rounded-xl p-4">
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="metric-value text-xl text-foreground">₹1,999</span>
                  <span className="text-xs text-muted-foreground">/mo</span>
                </div>
                <p className="text-xs font-semibold text-foreground mb-3">Monthly</p>
                <ul className="space-y-1.5">
                  {planFeatures.monthly.map((f) => (
                    <li key={`mf-${f}`} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                      <Check size={11} className="text-emerald-400 shrink-0" />{f}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-card/80 border border-border/60 rounded-xl p-4 relative">
                <div className="absolute -top-2 right-3 bg-primary text-primary-foreground text-[10px] font-bold px-2 py-0.5 rounded-full">SAVE ₹2,000</div>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="metric-value text-xl text-foreground">₹19,999</span>
                  <span className="text-xs text-muted-foreground">/yr</span>
                </div>
                <p className="text-xs font-semibold text-foreground mb-3">Yearly <Star size={11} className="inline text-yellow-400" /></p>
                <ul className="space-y-1.5">
                  {planFeatures.yearly.map((f) => (
                    <li key={`yf-${f}`} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                      <Check size={11} className="text-yellow-400 shrink-0" />{f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right auth panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-10 overflow-y-auto scrollbar-thin bg-background">
        <div className="flex items-center gap-2 mb-8 lg:hidden">
          <AppLogo size={32} />
          <span className="font-bold text-lg text-foreground">PropdataX</span>
        </div>

        <div className="w-full max-w-md">
          {/* Tab switcher */}
          <div className="flex bg-muted/60 rounded-xl p-1 mb-8">
            {(['login', 'signup'] as const).map((t) => (
              <button
                key={`tab-${t}`}
                onClick={() => setTab(t)}
                className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${tab === t ? 'bg-card text-primary shadow-sm border border-border' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {t === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          {/* Login form */}
          {tab === 'login' && (
            <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-5 fade-in">
              <div>
                <label className="label-base">Email Address</label>
                <div className="relative">
                  <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input type="email" placeholder="rajesh.mehta@propdatax.com" className="input-base pl-9"
                    {...loginForm.register('email', { required: 'Email is required', pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' } })} />
                </div>
                {loginForm.formState.errors.email && <p className="text-destructive text-xs mt-1">{loginForm.formState.errors.email.message}</p>}
              </div>

              <div>
                <label className="label-base">Password</label>
                <div className="relative">
                  <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input type={showPassword ? 'text' : 'password'} placeholder="Enter your password" className="input-base pl-9 pr-10"
                    {...loginForm.register('password', { required: 'Password is required' })} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                {loginForm.formState.errors.password && <p className="text-destructive text-xs mt-1">{loginForm.formState.errors.password.message}</p>}
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 rounded bg-input border-border accent-primary" {...loginForm.register('remember')} />
                  <span className="text-sm text-muted-foreground">Remember me</span>
                </label>
                <button type="button" className="text-sm text-primary hover:text-primary/80 transition-colors">Forgot password?</button>
              </div>

              <button type="submit" disabled={loginLoading || loginCooldown > 0}
                className="btn-primary w-full flex items-center justify-center gap-2 py-3 disabled:opacity-60 disabled:cursor-not-allowed">
                {loginLoading ? <><svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" /></svg>Signing in…</> :
                  loginCooldown > 0 ? <><Lock size={16} />Wait {loginCooldown}s before retrying</> :
                    <><span>Sign In to PropdataX</span><ChevronRight size={16} /></>}
              </button>

              <div className="mt-6 p-4 bg-muted/40 border border-border rounded-xl">
                <div className="flex items-center gap-2 mb-3">
                  <Shield size={14} className="text-primary" />
                  <span className="text-xs font-semibold text-primary uppercase tracking-wide">Demo Credentials</span>
                </div>
                <div className="space-y-2">
                  {demoCredentials.map((cred) => (
                    <div key={`cred-${cred.role}`} className="flex items-center justify-between p-2.5 bg-card rounded-lg border border-border">
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${cred.role === 'Admin' ? 'bg-red-900/40 text-red-400' : 'bg-primary/10 text-primary'}`}>{cred.role}</span>
                        <div>
                          <p className="text-xs font-medium text-foreground font-mono">{cred.email}</p>
                          <p className="text-[10px] text-muted-foreground font-mono">••••••••</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button type="button" onClick={() => copyToClipboard(cred.email)} className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-primary transition-all" title="Copy email"><Copy size={12} /></button>
                        <button type="button" onClick={() => autofill(cred)} className="text-[11px] font-semibold text-primary hover:text-primary/80 px-2 py-1 rounded hover:bg-muted transition-all">Use</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </form>
          )}

          {/* Signup form */}
          {tab === 'signup' && (
            <form onSubmit={signupForm.handleSubmit(handleSignupSubmit)} className="space-y-4 fade-in">
              {/* Profile Type Selection */}
              <div>
                <label className="label-base mb-2 block">Select Profile Type</label>
                <div className="grid grid-cols-3 gap-2">
                  {profileTypes.map(({ type, label, icon: Icon, desc, color }) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => { setSelectedProfileType(type); signupForm.setValue('profileType', type); setReferralStatus('idle'); }}
                      className={`relative p-3 rounded-xl border text-left transition-all duration-200 ${selectedProfileType === type ? 'border-primary bg-primary/10 ring-1 ring-primary/30' : 'border-border bg-card hover:border-primary/40'}`}
                    >
                      <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center mb-2`}>
                        <Icon size={14} className="text-white" />
                      </div>
                      <p className="text-[11px] font-bold text-foreground leading-tight">{label}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5 leading-tight">{desc}</p>
                      {selectedProfileType === type && (
                        <div className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-primary flex items-center justify-center">
                          <Check size={9} className="text-white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="label-base">Full Name</label>
                <div className="relative">
                  <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input type="text" placeholder="Priya Sharma" className="input-base pl-9"
                    {...signupForm.register('fullName', { required: 'Full name is required' })} />
                </div>
                {signupForm.formState.errors.fullName && <p className="text-destructive text-xs mt-1">{signupForm.formState.errors.fullName.message}</p>}
              </div>

              <div>
                <label className="label-base">Email Address</label>
                <div className="relative">
                  <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input type="email" placeholder="priya@realtyconsult.in" className="input-base pl-9"
                    {...signupForm.register('email', { required: 'Email is required', pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' } })} />
                </div>
                {signupForm.formState.errors.email && <p className="text-destructive text-xs mt-1">{signupForm.formState.errors.email.message}</p>}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-base">Phone Number</label>
                  <div className="relative">
                    <Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input type="tel" placeholder="+91 98765 43210" className="input-base pl-9"
                      {...signupForm.register('phone', { required: 'Phone is required' })} />
                  </div>
                  {signupForm.formState.errors.phone && <p className="text-destructive text-xs mt-1">{signupForm.formState.errors.phone.message}</p>}
                </div>
                <div>
                  <label className="label-base">City</label>
                  <div className="relative">
                    <MapPin size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input type="text" placeholder="Pune" className="input-base pl-9"
                      {...signupForm.register('city')} />
                  </div>
                </div>
              </div>

              <div>
                <label className="label-base">Password</label>
                <div className="relative">
                  <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input type={showPassword ? 'text' : 'password'} placeholder="Create a strong password" className="input-base pl-9 pr-10"
                    {...signupForm.register('password', { required: 'Password is required', minLength: { value: 8, message: 'Minimum 8 characters' } })} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                {signupForm.formState.errors.password && <p className="text-destructive text-xs mt-1">{signupForm.formState.errors.password.message}</p>}
              </div>

              <div>
                <label className="label-base">Confirm Password</label>
                <div className="relative">
                  <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input type={showConfirmPassword ? 'text' : 'password'} placeholder="Repeat your password" className="input-base pl-9 pr-10"
                    {...signupForm.register('confirmPassword', { required: 'Please confirm your password' })} />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {showConfirmPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                {signupForm.formState.errors.confirmPassword && <p className="text-destructive text-xs mt-1">{signupForm.formState.errors.confirmPassword.message}</p>}
              </div>

              {/* Referral ID — only for end_user and channel_partner */}
              {showReferralField && (
                <div>
                  <label className="label-base">
                    Referral ID
                    <span className="ml-1.5 text-[10px] text-muted-foreground font-normal">(optional)</span>
                  </label>
                  <div className="relative">
                    <Zap size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="e.g. AMRUT001"
                      className={`input-base pl-9 pr-10 uppercase ${referralStatus === 'valid' ? 'border-emerald-500/50 focus:border-emerald-500' : referralStatus === 'invalid' ? 'border-red-500/50 focus:border-red-500' : ''}`}
                      {...signupForm.register('referralCode', {
                        onChange: (e) => {
                          const code = e.target.value;
                          const email = signupForm.getValues('email');
                          if (code.length >= 4) validateReferralCode(code, email);
                          else setReferralStatus('idle');
                        }
                      })}
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {referralValidating && <Loader2 size={14} className="animate-spin text-muted-foreground" />}
                      {!referralValidating && referralStatus === 'valid' && <CheckCircle2 size={14} className="text-emerald-400" />}
                      {!referralValidating && referralStatus === 'invalid' && <AlertCircle size={14} className="text-red-400" />}
                    </div>
                  </div>
                  {referralStatus === 'valid' && referralReferrerName && (
                    <p className="text-emerald-400 text-xs mt-1 flex items-center gap-1"><CheckCircle2 size={11} /> Referred by {referralReferrerName}</p>
                  )}
                  {referralStatus === 'invalid' && (
                    <p className="text-red-400 text-xs mt-1 flex items-center gap-1"><AlertCircle size={11} /> Invalid Referral ID</p>
                  )}
                </div>
              )}

              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 mt-0.5 rounded bg-input border-border accent-primary"
                  {...signupForm.register('terms', { required: 'You must accept terms' })} />
                <span className="text-sm text-muted-foreground leading-snug">
                  I agree to the{' '}
                  <button type="button" className="text-primary hover:text-primary/80">Terms of Service</button>
                  {' '}and{' '}
                  <button type="button" className="text-primary hover:text-primary/80">Privacy Policy</button>
                </span>
              </label>
              {signupForm.formState.errors.terms && <p className="text-destructive text-xs">{signupForm.formState.errors.terms.message}</p>}

              <button type="submit" disabled={signupLoading}
                className="btn-primary w-full flex items-center justify-center gap-2 py-3">
                {signupLoading ? <><svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" /></svg>Creating account…</> :
                  <><Zap size={16} />Create Account & Select Plan</>}
              </button>

              <p className="text-center text-xs text-muted-foreground">
                Already have an account?{' '}
                <button type="button" onClick={() => setTab('login')} className="text-primary hover:text-primary/80">Sign in</button>
              </p>
            </form>
          )}
        </div>

        <div className="mt-8 flex items-center gap-4 text-[11px] text-muted-foreground/60">
          <span className="flex items-center gap-1"><Users size={11} /> 1,840+ subscribers</span>
          <span className="flex items-center gap-1"><Building2 size={11} /> 214 projects</span>
          <span className="flex items-center gap-1"><BarChart3 size={11} /> Live data</span>
        </div>
      </div>
    </div>
  );
}